import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { switchboardApi } from '~/apis/switchboardApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { useCheckRole } from '~/hooks/useCheckRole'
import { ApplicationRoutes } from '~/types/RoutingTypes'

import { SwitchboardListPage } from '@mangovoice/switchboard-ui'

const EXTENSIONS_ALL_LIMIT = 10000

/**
 * Switchboard List Page for web-app
 * Displays a paginated grid of switchboards with create/edit/delete functionality
 * Uses /app/v1/extensions/ to match admin3 exactly.
 */
export const SwitchboardPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pbxId } = useUserState({ canAutoRefresh: false })
  const checkRole = useCheckRole()
  const canDelete = checkRole('senior')

  const handleFetchExtensions = useCallback(
    async (fetchParams: {
      pbx_id: number
      type: 'ExtensionDialPlan'
      limit?: number
      offset?: number
    }) => {
      const response = await switchboardApi.listExtensions({
        pbx_id: fetchParams.pbx_id,
        type: fetchParams.type,
        limit: fetchParams.limit || EXTENSIONS_ALL_LIMIT,
        ...(fetchParams.offset !== undefined && {
          offset: fetchParams.offset,
        }),
      })

      if (response.ok) {
        return {
          ok: true as const,
          data: response.data,
          status: response.status,
        }
      }

      return {
        ok: false,
        data: undefined,
        status: response.status,
      }
    },
    [],
  )

  const getEditRoute = useCallback((extensionDialPlan: number) => {
    return ApplicationRoutes.SWITCHBOARD_EDIT.replace(
      ':extensionDialPlan',
      String(extensionDialPlan),
    )
  }, [])

  if (!pbxId) {
    return null
  }

  return (
    <SwitchboardListPage
      api={switchboardApi}
      navigate={navigate}
      editRouteTemplate={ApplicationRoutes.SWITCHBOARD_EDIT}
      pbxId={pbxId}
      toast={toaster}
      fetchExtensions={handleFetchExtensions}
      getEditRoute={getEditRoute}
      listRoute={ApplicationRoutes.SWITCHBOARDS}
      canDelete={canDelete}
      t={t}
    />
  )
}
